import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { Search, FileChartLine, Image as ImageIcon, Video as VideoIcon } from 'lucide-react';
import ClientBottomNav from '../../components/ClientBottomNav.tsx';
import { WorkStation } from '../../Interfaces/workstation.ts';
import { mergeWorkInstructionRecords, MergedWorkInstruction, workInstructionVMF } from '../../utils/workInstructionVMF.ts';
import { baseurl, X_API_KEY, baseurly } from '../../utils/apis.ts';
import MainLayout from '../../layouts/MainLayout.tsx';

const WorkInstructionsViewPage001: React.FC = () => {
  const { vehicleId, buildingId, workstationId } = useParams();
  const navigate = useNavigate();

  const [workInstructions, setWorkInstructions] = useState<MergedWorkInstruction[]>([]);
  const [workStation, setWorkStation] = useState<WorkStation | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showVideoModal, setShowVideoModal] = useState<boolean>(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filteredInstructions, setFilteredInstructions] = useState<MergedWorkInstruction[]>([]);

  useEffect(() => {
    if (workstationId && vehicleId) {
      fetch(`${baseurly}/work_station/${workstationId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-API-KEY': X_API_KEY,
        },
        credentials: 'include',
      })
        .then((res) =>
          res.ok ? res.json() : Promise.reject('Failed to fetch work instructions')
        )
        .then((data: WorkStation) => {
          setWorkStation(data);
          const mergedInstructions = mergeWorkInstructionRecords(
            workInstructionVMF(
              data.work_instructions,
              parseInt(workstationId, 10),
              parseInt(vehicleId!, 10)
            )
          );
          setWorkInstructions(mergedInstructions);
          setFilteredInstructions(mergedInstructions);
          setLoading(false);
        })
        .catch((err) => {
          console.error('Error fetching work instructions:', err);
          setError('Unable to load work instructions');
          setLoading(false);
        });
    }
  }, [vehicleId, workstationId]);

  useEffect(() => {
    if (searchTerm) {
      const normalizedSearch = searchTerm.toLowerCase();
      const results = workInstructions.filter((instruction) =>
        instruction.title.toLowerCase().includes(normalizedSearch) ||
        instruction.description.toLowerCase().includes(normalizedSearch)
      );
      setFilteredInstructions(results);
    } else {
      setFilteredInstructions(workInstructions);
    }
  }, [searchTerm, workInstructions]);

  const handleInstructionView = (instructionId: number) => {
    navigate(
      `/vehicle/${vehicleId}/building/${buildingId}/workstation/${workstationId}/workinstruction/${instructionId}`
    );
  };

  const handleDrawingView = (instructionId: number) => {
    navigate(
      `/vehicle/${vehicleId}/building/${buildingId}/workstation/${workstationId}/drawing/${instructionId}`
    );
  };

  const handleVideoView = (videoPath: string) => {
    const fullVideoUrl = `${baseurl}files/${videoPath}`;
    setVideoUrl(fullVideoUrl);
    setShowVideoModal(true);
  };

  const instructionSummary = {
    total: filteredInstructions.length,
    withPdf: filteredInstructions.filter((instruction) => instruction.file).length,
    withDrawing: filteredInstructions.filter((instruction) => instruction.drawing).length,
    withVideo: filteredInstructions.filter((instruction) => instruction.video).length,
  };

  return (
    <MainLayout>
      <div className="min-h-screen bg-blue-900 pb-20">
        <div className="px-6 pt-6">
          
          <h1 className="text-white text-2xl font-bold mt-1">Work Instructions</h1>
        </div>

        <div className="px-6 mb-6">
          <div className="relative">
            <input
              type="text"
              placeholder="Search work instructions..."
              className="w-full py-3 px-4 pr-10 rounded-2xl border border-white/20 bg-white/95 shadow-sm outline-none transition focus:border-blue-300 focus:ring-2 focus:ring-blue-200"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
          </div>
        </div>

        <div className="px-6 mb-6">
          <div className="rounded-[2rem] bg-white p-6 shadow-xl ring-1 ring-blue-100">
            <div className="rounded-[1.5rem] border border-blue-100 bg-gradient-to-r from-white to-sky-50 p-8">
              <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
                <div className="max-w-2xl">
                  <p className="text-sm font-semibold uppercase tracking-[0.2em] text-blue-500">
                    {workStation?.work_station || 'Workstation'}
                  </p>
                  <h2 className="mt-2 text-2xl font-bold text-blue-950">
                    Instruction Files
                  </h2>
                  <p className="mt-3 text-sm leading-6 text-slate-600">
                    Browse the latest files for this station in one place. Each card shows the instruction title,
                    description, and whichever instruction PDF, drawing, or video files are available.
                  </p>
                </div>
                <div className="flex flex-wrap gap-3">
                  {/* <div className="min-w-[110px] rounded-2xl bg-blue-950 px-4 py-3 text-white shadow-md">
                    <p className="text-xs uppercase tracking-wide text-blue-100">Rows</p>
                    <p className="mt-1 text-2xl font-bold">{instructionSummary.total}</p>
                  </div> */}
                  <div className="min-w-[110px] rounded-2xl bg-emerald-50 px-4 py-3 text-emerald-900 ring-1 ring-emerald-100">
                    <p className="text-xs uppercase tracking-wide text-emerald-600">Drawings</p>
                    <p className="mt-1 text-2xl font-bold">{instructionSummary.withDrawing}</p>
                  </div>
                  <div className="min-w-[110px] rounded-2xl bg-blue-50 px-4 py-3 text-blue-900 ring-1 ring-blue-100">
                    <p className="text-xs uppercase tracking-wide text-blue-600">Work Instruction</p>
                    <p className="mt-1 text-2xl font-bold">{instructionSummary.withPdf}</p>
                  </div>
                  <div className="min-w-[110px] rounded-2xl bg-violet-50 px-4 py-3 text-violet-900 ring-1 ring-violet-100">
                    <p className="text-xs uppercase tracking-wide text-violet-600">Videos</p>
                    <p className="mt-1 text-2xl font-bold">{instructionSummary.withVideo}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 rounded-[1.5rem] border border-slate-200 bg-slate-50/70 p-6">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <h3 className="text-xl font-bold text-blue-950">Available Files</h3>
                  <p className="text-sm text-slate-600">
                    Clean, direct access to instruction PDFs, drawings, and videos for this workstation.
                  </p>
                </div>
                {searchTerm && (
                  <p className="text-sm text-slate-500">
                    Showing results for <span className="font-semibold text-blue-900">"{searchTerm}"</span>
                  </p>
                )}
              </div>

              {loading ? (
                <p className="mt-6 text-gray-600">Loading work instructions...</p>
              ) : error ? (
                <p className="mt-6 text-red-500">{error}</p>
              ) : filteredInstructions.length > 0 ? (
                <ul className="mt-6 space-y-4">
                  {filteredInstructions.map((instruction) => {
                    const instructionId = instruction.instructionId;
                    const drawingInstructionId = instruction.drawingInstructionId;
                    const availableActions = [
                      instruction.file && instructionId !== null ? (
                        <button
                          key="instruction"
                          onClick={() => handleInstructionView(instructionId)}
                          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
                        >
                          <FileChartLine size={18} />
                          View Instruction
                        </button>
                      ) : null,
                      instruction.drawing && drawingInstructionId !== null ? (
                        <button
                          key="drawing"
                          onClick={() => handleDrawingView(drawingInstructionId)}
                          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700"
                        >
                          <ImageIcon size={18} />
                          View Drawing
                        </button>
                      ) : null,
                      instruction.video ? (
                        <button
                          key="video"
                          onClick={() => handleVideoView(instruction.video!)}
                          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-violet-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-violet-700"
                        >
                          <VideoIcon size={18} />
                          View Video
                        </button>
                      ) : null,
                    ].filter(Boolean);

                    return (
                      <li
                        key={instruction.id}
                        className="rounded-[1.5rem] border border-blue-100 bg-white p-5 shadow-sm transition hover:shadow-md"
                      >
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <h4 className="text-xl font-bold text-blue-950">{instruction.title}</h4>
                              {instruction.file && (
                                <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-800">
                                  PDF
                                </span>
                              )}
                              {instruction.drawing && (
                                <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-800">
                                  Drawing
                                </span>
                              )}
                              {instruction.video && (
                                <span className="rounded-full bg-violet-100 px-3 py-1 text-xs font-semibold text-violet-800">
                                  Video
                                </span>
                              )}
                            </div>

                            <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600">
                              {instruction.description}
                              {}
                            </p>
                          </div>

                          <div className="rounded-2xl bg-slate-50 px-4 py-3 ring-1 ring-slate-200 lg:min-w-[240px]">
                            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                              Available Files
                            </p>
                            <div className="mt-3 flex flex-wrap gap-2">
                              {instruction.file && (
                                <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-800">
                                  Instruction ready
                                </span>
                              )}
                              {instruction.drawing && (
                                <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-800">
                                  Drawing ready
                                </span>
                              )}
                              {instruction.video && (
                                <span className="rounded-full bg-violet-100 px-3 py-1 text-xs font-semibold text-violet-800">
                                  Video ready
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {availableActions.length > 0 && (
                          <div className="mt-5 grid gap-3 md:grid-cols-3">
                            {availableActions}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="mt-6 rounded-3xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center">
                  <p className="text-lg font-semibold text-slate-700">No work instructions available.</p>
                  <p className="mt-2 text-sm text-slate-500">
                    Try a different search or check back after new files are added to this workstation.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {showVideoModal && videoUrl && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
            <div className="w-full max-w-3xl rounded-3xl bg-white p-6 shadow-2xl">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-900">Instruction Video</h2>
                <button
                  onClick={() => setShowVideoModal(false)}
                  className="rounded-full bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-200"
                >
                  Close
                </button>
              </div>
              <iframe
                src={videoUrl}
                title="Work Instruction Video"
                className="h-[420px] w-full rounded-2xl"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        )}

        <ClientBottomNav showBack={true} />
      </div>
    </MainLayout>
  );
};

export default WorkInstructionsViewPage001;
